"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.treeItemId = exports.TreeWidget = exports.LoadingItemContent = void 0;
var tslib_1 = require("tslib");
var ArrowChevronRightIcon_1 = require("@tableau/icons/ArrowChevronRightIcon");
var NotificationCheckIcon_1 = require("@tableau/icons/NotificationCheckIcon");
var OperationsSubtractMinusIcon_1 = require("@tableau/icons/OperationsSubtractMinusIcon");
var React = tslib_1.__importStar(require("react"));
var TypeStyle = tslib_1.__importStar(require("typestyle"));
var Localize_1 = require("../../../gen-src/Localize");
var Declarative_1 = require("../../utils/Declarative");
var DomUtil_1 = require("../../utils/DomUtil");
var LiveAnnouncer_1 = require("../../utils/LiveAnnouncer");
var ReactHelpers_1 = require("../../utils/ReactHelpers");
var ActivitySpinnerWidget_1 = require("../ActivitySpinnerWidget");
var FilteringList_1 = require("../List/FilteringList");
var List_1 = require("../List/List");
var ListItemUtils_1 = require("../List/ListItemUtils");
var ListStyles_1 = require("../List/ListStyles");
var TextFieldWidget_1 = require("../TextField/TextFieldWidget");
/* eslint-disable max-len */
var TreeHelpers_1 = require("./TreeHelpers");
Object.defineProperty(exports, "LoadingItemContent", { enumerable: true, get: function () { return TreeHelpers_1.LoadingItemContent; } });
var TreeStyles_1 = require("./TreeStyles");
/**
 * `TreeWidget` allows users to browse and select from items nested in a hierarchy.
 * Can be used as either a controlled or uncontrolled component when managing expansion state and/or selection state.
 *
 * In controlled mode, the parent component is responsible for handling changes signaled by the callback props
 * and passing in the props: `expandedValues`, `selectedValue` or `selectedValues`.
 * In uncontrolled mode, the state is handled internally by `TreeWidget` but can be accessed by saving a ref
 * and can be defaulted by passing in the props: `initiallyExpandedValues`, `initiallySelectedValue` or `initiallySelectedValues`.
 *
 * All HTML props are forwarded to the root wrapper element, with the following exceptions:
 * - `autoComplete` is applied to the search field `<input>` element
 * - `aria-describedby` is applied to either the search field `<input>` element, or the `role=tree` element when `hideSearchField` is set to true.
 * - `aria-label` and `aria-labelledby` are applied to both the search field `<input>` element and the `role=tree` element
 */
function TreeWidget(props) {
    // eslint-disable-next-line max-len
    var ariaDescribedby = props["aria-describedby"], ariaLabel = props["aria-label"], ariaLabelledby = props["aria-labelledby"], autoComplete = props.autoComplete, autoFocus = props.autoFocus, expandedValues = props.expandedValues, hideSearchField = props.hideSearchField, initiallyExpandedValues = props.initiallyExpandedValues, initiallySelectedValue = props.initiallySelectedValue, initiallySelectedValues = props.initiallySelectedValues, itemFilterFn = props.itemFilterFn, items = props.items, label = props.label, multiselect = props.multiselect, onExpansionChange = props.onExpansionChange, onSearchTextChange = props.onSearchTextChange, onSelectionChange = props.onSelectionChange, parentNodeActivationAction = props.parentNodeActivationAction, searchPlaceholder = props.searchPlaceholder, searchTextControlled = props.searchText, selectedValue = props.selectedValue, selectedValues = props.selectedValues, testId = props.testId, treeElemAttrs = props.treeElemAttrs, treeRef = props.treeRef, htmlAttrs = tslib_1.__rest(props, ['aria-describedby', 'aria-label', 'aria-labelledby', "autoComplete", "autoFocus", "expandedValues", "hideSearchField", "initiallyExpandedValues", "initiallySelectedValue", "initiallySelectedValues", "itemFilterFn", "items", "label", "multiselect", "onExpansionChange", "onSearchTextChange", "onSelectionChange", "parentNodeActivationAction", "searchPlaceholder", "searchText", "selectedValue", "selectedValues", "testId", "treeElemAttrs", "treeRef"]);
    var treeLabelId = React.useState(DomUtil_1.generateId())[0];
    var _a = React.useState(null), scrollRequest = _a[0], setScrollRequest = _a[1]; // used for deferred scrolling
    var isComposing = React.useRef(false);
    var _b = React.useState(''), searchTextInternal = _b[0], setSearchText = _b[1];
    var searchText = hideSearchField ? '' : searchTextControlled !== null && searchTextControlled !== void 0 ? searchTextControlled : searchTextInternal; // always use empty string when search field is not shown
    var searchFieldRef = React.useRef(null);
    var treeElemRef = React.useRef(null);
    var _c = useDebouncedAnnouceCount(searchFieldRef, treeElemRef), announceCount = _c.announceCount, clearAnnounceCount = _c.clearAnnounceCount;
    var _d = useTree(props, searchText), treeAttributes = _d.treeAttributes, treeHierarchyInfo = _d.treeHierarchyInfo, treeState = _d.treeState;
    React.useEffect(function () {
        window.setTimeout(function () {
            var _a;
            autoFocus && ((_a = searchFieldRef.current) === null || _a === void 0 ? void 0 : _a.focus());
        });
        // intentionally using empty dependencies to run this effect only once on mount
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    ReactHelpers_1.useEffectExceptOnMount(function () {
        items && document.activeElement === searchFieldRef.current ? announceCount() : clearAnnounceCount();
        return function () { return clearAnnounceCount(); };
    }, [announceCount, clearAnnounceCount, items, searchFieldRef, searchText]);
    ReactHelpers_1.useEffectExceptOnMount(function () {
        onSearchTextChange === null || onSearchTextChange === void 0 ? void 0 : onSearchTextChange({ isComposing: isComposing.current, target: { value: searchTextInternal } });
    }, [searchTextInternal]);
    React.useImperativeHandle(treeRef, function () { return ({
        /* Properties */
        get activeValue() {
            return treeState.activeValue;
        },
        set activeValue(newActiveValue) {
            treeState.updateActiveValue(newActiveValue);
        },
        get expandedValues() {
            return treeState.expandedValues;
        },
        set expandedValues(newExpandedValues) {
            treeState.setExpandedValues(newExpandedValues);
        },
        get selectedValue() {
            return treeState.selectedValues.size === 1 ? treeState.selectedValues.keys().next().value : undefined;
        },
        set selectedValue(newSelectedValue) {
            !multiselect && treeState.setSelectedValues(newSelectedValue ? new Set(newSelectedValue) : new Set());
        },
        get selectedValues() {
            return treeState.selectedValues;
        },
        set selectedValues(newSelectedValues) {
            multiselect && treeState.setSelectedValues(newSelectedValues);
        },
        /* Methods */
        expandAll: function () { return treeState.setExpandedValues(treeHierarchyInfo.allParentValues); },
        scrollIntoView: function (value, block) {
            treeState.addToExpandedValues(TreeHelpers_1.getAncestorValues(treeHierarchyInfo.nodeMapByValue, value));
            // The previous line may result in a change to expansion state in order to show the item with the given value,
            // and since state update is async, we need to rely on the way React batches state updates and trigger the scroll effect after the next render.
            setScrollRequest({ value: value, block: block !== null && block !== void 0 ? block : 'nearest' });
        },
        selectAll: function () { return multiselect && treeState.setSelectedValues(treeHierarchyInfo.allSelectableLeafValues); },
        toggleExpansion: function (value, expand) { return treeState.toggleExpansion(value, expand); },
        toggleSelection: function (value, select) { return treeState.toggleSelection(value, select); },
    }); });
    React.useEffect(function deferredScrolling() {
        if (scrollRequest) {
            DomUtil_1.scrollIntoView(document.getElementById(exports.treeItemId(treeAttributes.id, scrollRequest.value)), scrollRequest.block);
        }
        // intentionally only depend on `scrollRequest` which gets updated in the `scrollIntoView` instance method in order to defer scrolling
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollRequest]);
    var handleContainerFocusCapture = React.useCallback(function (e) {
        // NOTE: this focus handler actually causes a bug for mouse interactions because the click handler never gets called when a scroll-into-view event
        // happens before the mouse click finishes, but we are not fixing this right now since real users don't need to scroll to see the search field.
        // For details see https://salesforce-internal.slack.com/archives/G9MFJED2Q/p1720720853982399.
        if (searchFieldRef.current && e.target !== searchFieldRef.current) {
            searchFieldRef.current.focus(); // make sure focus stays on the search field when clicking on items
        }
    }, [searchFieldRef]);
    var handleContainerKeyDown = React.useCallback(function (e) {
        var _a;
        var activeValue = treeState.activeValue, expandedValues = treeState.expandedValues, focusableValues = treeState.focusableValues, toggleExpansion = treeState.toggleExpansion, toggleSelection = treeState.toggleSelection, updateActiveValue = treeState.updateActiveValue;
        if (!focusableValues.length) {
            return;
        } // early return because there's no focusable item to interact with
        if (e.key === ' ' && !hideSearchField) {
            return;
        } // early return because space should be treated as a regular character in the search field
        if (!activeValue) {
            if (e.key === 'ArrowDown' && !hideSearchField) {
                updateActiveValue(focusableValues[0]); // special case for initializing pseudo focus
                e.preventDefault();
                e.stopPropagation();
            }
        }
        else {
            var currentTreeNode_1 = treeHierarchyInfo.nodeMapByValue.get(activeValue);
            var indexInAllFocusables_1 = focusableValues.indexOf(activeValue);
            var isExpanded_1 = expandedValues.has(activeValue);
            var focusableChildNodes_1 = currentTreeNode_1 === null || currentTreeNode_1 === void 0 ? void 0 : currentTreeNode_1.children.filter(function (node) { return ListItemUtils_1.isFocusableItem(node.item); });
            var firstFocusableChildValue_1 = (_a = focusableChildNodes_1 === null || focusableChildNodes_1 === void 0 ? void 0 : focusableChildNodes_1.find(function (node) { return focusableValues.includes(node.item.value); })) === null || _a === void 0 ? void 0 : _a.item.value;
            var toggleSelectionOrExpansion = (parentNodeActivationAction === 'toggleExpansion' && (currentTreeNode_1 === null || currentTreeNode_1 === void 0 ? void 0 : currentTreeNode_1.children.length))
                ? (function (e) { return !e.repeat && toggleExpansion(activeValue); })
                : (function (e) { return !e.repeat && toggleSelection(activeValue); });
            var keyDownHandlers = Object.freeze({
                ' ': toggleSelectionOrExpansion,
                'Enter': toggleSelectionOrExpansion,
                'ArrowRight': function () {
                    if (focusableChildNodes_1 === null || focusableChildNodes_1 === void 0 ? void 0 : focusableChildNodes_1.length) {
                        if (isExpanded_1) {
                            firstFocusableChildValue_1 && updateActiveValue(firstFocusableChildValue_1);
                        }
                        else {
                            toggleExpansion(activeValue, true);
                        }
                    }
                },
                'ArrowLeft': function () {
                    if (isExpanded_1) {
                        toggleExpansion(activeValue, false);
                    }
                    else {
                        var parentValue = currentTreeNode_1 === null || currentTreeNode_1 === void 0 ? void 0 : currentTreeNode_1.item.parentValue;
                        parentValue && updateActiveValue(parentValue);
                    }
                },
                'ArrowUp': function () { return updateActiveValue(focusableValues[Math.max(0, indexInAllFocusables_1 - 1)]); },
                'ArrowDown': function () { return updateActiveValue(focusableValues[Math.min(focusableValues.length - 1, indexInAllFocusables_1 + 1)]); },
                'Home': function () { return updateActiveValue(focusableValues[0]); },
                'End': function () { return updateActiveValue(focusableValues[focusableValues.length - 1]); },
            });
            if (keyDownHandlers[e.key]) {
                keyDownHandlers[e.key](e);
                e.preventDefault();
                e.stopPropagation();
            }
        }
    }, [hideSearchField, parentNodeActivationAction, treeHierarchyInfo.nodeMapByValue, treeState]);
    var children = useTreeChildElements(treeAttributes.id, treeHierarchyInfo, treeState, multiselect);
    var descriptionIds = ariaDescribedby;
    if (!items) {
        // need a hidden description because the alt text in `ActivitySpinnerWidget` does not appear as part of the accessible name
        var hiddenDescriptionId = treeAttributes.id + "%%loading-description";
        descriptionIds = ariaDescribedby ? ariaDescribedby + " " + hiddenDescriptionId : hiddenDescriptionId;
        children = React.createElement(React.Fragment, null,
            React.createElement("span", { className: TreeStyles_1.SpinnerWrapperStyleName }, React.createElement(ActivitySpinnerWidget_1.ActivitySpinnerWidget, { testId: testId !== null && testId !== void 0 ? testId : '' })),
            React.createElement("span", { id: hiddenDescriptionId, style: LiveAnnouncer_1.VisuallyHiddenStyle }, Localize_1.Messages.loading()));
    }
    else if (searchText && !treeState.matchedValues.size) {
        children = React.createElement("span", null, FilteringList_1.FilteringListMessages.noMatches());
    }
    var treeLabelIds = ariaLabelledby;
    var showTreeLabel = hideSearchField && label;
    if (showTreeLabel) {
        treeLabelIds = ariaLabelledby ? ariaLabelledby + " " + treeLabelId : treeLabelId;
    }
    return React.createElement("div", tslib_1.__assign({ "data-tb-test-id": Declarative_1.makeTestId(testId, TreeWidget.displayName) }, htmlAttrs, { className: TypeStyle.classes(TreeStyles_1.RootContainerStyleName, htmlAttrs.className), onFocusCapture: ReactHelpers_1.mergeEventHandlers(handleContainerFocusCapture, htmlAttrs.onFocusCapture), onKeyDown: ReactHelpers_1.mergeEventHandlers(handleContainerKeyDown, htmlAttrs.onKeyDown) }),
        !hideSearchField &&
            React.createElement(TextFieldWidget_1.TextFieldWidget, { "aria-activedescendant": treeState.activeValue ? exports.treeItemId(treeAttributes.id, treeState.activeValue) : undefined, "aria-controls": treeAttributes.id, "aria-describedby": descriptionIds, "aria-expanded": true, "aria-haspopup": 'tree', "aria-label": ariaLabel, "aria-labelledby": ariaLabelledby, autoComplete: autoComplete, className: TreeStyles_1.SearchFieldStyleName, kind: 'search', label: label, onBlur: function () {
                    clearAnnounceCount();
                    treeState.updateActiveValue(undefined);
                }, onChange: function (e) {
                    isComposing.current = e.isComposing;
                    setSearchText(e.target.value);
                }, onClear: function () {
                    isComposing.current = false;
                    setSearchText('');
                }, onFocus: function () { return items && announceCount(5000); }, placeholder: searchPlaceholder, ref: searchFieldRef, role: 'combobox', testId: testId || '', value: searchText }),
        showTreeLabel && React.createElement("label", { id: treeLabelId }, label),
        React.createElement(List_1.ListIsActiveContext.Consumer, null, function (listIsActive) {
            var _a;
            return React.createElement("ul", tslib_1.__assign({}, ReactHelpers_1.EventUtilsOptOut, { tabIndex: hideSearchField ? 0 : -1 }, treeElemAttrs, { "aria-describedby": hideSearchField ? descriptionIds : undefined, "aria-label": ariaLabel, "aria-labelledby": treeLabelIds, children: children, className: TypeStyle.classes(TreeStyles_1.TreeStyleName, (_a = {},
                    _a[ListStyles_1.ListIsActiveClass] = listIsActive,
                    _a[TreeStyles_1.TreeUnselectableParentsClassName] = parentNodeActivationAction === 'toggleExpansion',
                    _a), treeElemAttrs === null || treeElemAttrs === void 0 ? void 0 : treeElemAttrs.className), ref: treeElemRef, role: 'tree' }, treeAttributes));
        }));
}
exports.TreeWidget = TreeWidget;
TreeWidget.defaultProps = { parentNodeActivationAction: 'toggleSelection' };
TreeWidget.displayName = 'TreeWidget';
var treeItemId = function (treeId, itemValue) {
    return treeId + "%%" + itemValue;
};
exports.treeItemId = treeItemId;
var useTree = function (props, searchText) {
    var _a;
    var _b;
    var hideSearchField = props.hideSearchField, itemFilterFn = props.itemFilterFn, items = props.items, multiselect = props.multiselect, parentNodeActivationAction = props.parentNodeActivationAction, treeElemAttrs = props.treeElemAttrs;
    var defaultDomId = React.useState(DomUtil_1.generateId())[0];
    var treeId = (_b = treeElemAttrs === null || treeElemAttrs === void 0 ? void 0 : treeElemAttrs.id) !== null && _b !== void 0 ? _b : defaultDomId;
    var treeHierarchyInfo = React.useMemo(function () { return TreeHelpers_1.constructTreeNodeHierarchy(items, parentNodeActivationAction === 'toggleExpansion', multiselect); }, [items, multiselect, parentNodeActivationAction]);
    var _c = React.useMemo(function () { return TreeHelpers_1.filterTree(treeHierarchyInfo.topLevelNodes, searchText, itemFilterFn); }, [itemFilterFn, searchText, treeHierarchyInfo.topLevelNodes]), matchedValues = _c.matchedValues, matchedValuesAncestors = _c.matchedValuesAncestors, topLevelNodes = _c.topLevelNodesFiltered;
    var _d = useSelectionState(treeHierarchyInfo, props), selectedValues = _d.selectedValues, setSelectedValues = _d.setSelectedValues, toggleSelection = _d.toggleSelection;
    var _e = useExpansionState(props, matchedValues, matchedValuesAncestors), addToExpandedValues = _e.addToExpandedValues, expandedValues = _e.expandedValues, setExpandedValues = _e.setExpandedValues, toggleExpansion = _e.toggleExpansion;
    var _f = React.useMemo(function () { return TreeHelpers_1.getFocusableValuesAndTreeHeight(topLevelNodes, expandedValues); }, [expandedValues, topLevelNodes]), focusableValues = _f.focusableValues, treeHeight = _f.treeHeight;
    var _g = usePseudoFocusState(focusableValues, selectedValues, treeId, hideSearchField), activeValue = _g.activeValue, updateActiveValue = _g.updateActiveValue;
    var handleClick = React.useCallback(function (e) {
        var _a;
        var item = e.target.closest('[role="treeitem"]');
        var itemValue = item === null || item === void 0 ? void 0 : item.getAttribute(List_1.ItemValueAttr);
        var isParent = item === null || item === void 0 ? void 0 : item.hasAttribute('aria-expanded');
        if (itemValue) {
            if (parentNodeActivationAction === 'toggleExpansion' && isParent) {
                toggleExpansion(itemValue);
            }
            else {
                toggleSelection(itemValue);
            }
            updateActiveValue(itemValue);
        }
        (_a = treeElemAttrs === null || treeElemAttrs === void 0 ? void 0 : treeElemAttrs.onClick) === null || _a === void 0 ? void 0 : _a.call(treeElemAttrs, e);
    }, [parentNodeActivationAction, toggleExpansion, toggleSelection, treeElemAttrs, updateActiveValue]);
    return {
        treeAttributes: {
            'aria-activedescendant': activeValue ? exports.treeItemId(treeId, activeValue) : undefined,
            'aria-multiselectable': multiselect,
            id: treeId,
            onClick: handleClick,
            style: tslib_1.__assign(tslib_1.__assign({}, treeElemAttrs === null || treeElemAttrs === void 0 ? void 0 : treeElemAttrs.style), (_a = {}, _a["" + TreeStyles_1.TreeHeightCSSVarName] = treeHeight, _a)),
        },
        treeHierarchyInfo: tslib_1.__assign(tslib_1.__assign({}, treeHierarchyInfo), { topLevelNodes: topLevelNodes }),
        treeState: {
            activeValue: activeValue,
            addToExpandedValues: addToExpandedValues,
            expandedValues: expandedValues,
            focusableValues: focusableValues,
            matchedValues: matchedValues,
            selectedValues: selectedValues,
            setExpandedValues: setExpandedValues,
            setSelectedValues: setSelectedValues,
            toggleExpansion: toggleExpansion,
            toggleSelection: toggleSelection,
            updateActiveValue: updateActiveValue,
        }
    };
};
var useDebouncedAnnouceCount = function (searchFieldRef, treeElemRef) {
    var announceTimeoutId = React.useRef(0);
    var announceCount = React.useCallback(function (timeout) {
        if (timeout === void 0) { timeout = 2000; }
        window.clearTimeout(announceTimeoutId.current);
        announceTimeoutId.current = window.setTimeout(function () {
            var _a, _b;
            var numItems = ((_a = treeElemRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('[role="treeitem"]').length) || 0;
            var message = FilteringList_1.FilteringListMessages.optionCountAnnouncement({ optionCount: numItems, formattedOptionCount: Localize_1.Formatters.formatCount(numItems) });
            if (((_b = searchFieldRef.current) === null || _b === void 0 ? void 0 : _b.value) && numItems === 0) {
                message = FilteringList_1.FilteringListMessages.noMatches();
            }
            LiveAnnouncer_1.LiveAnnouncer.announce(message);
        }, timeout);
    }, [searchFieldRef, treeElemRef]);
    var clearAnnounceCount = React.useCallback(function () {
        window.clearTimeout(announceTimeoutId.current);
    }, []);
    return { announceCount: announceCount, clearAnnounceCount: clearAnnounceCount };
};
var useExpansionState = function (props, matchedValues, matchedValuesAncestors) {
    var expandedValuesControlled = props.expandedValues, initiallyExpandedValues = props.initiallyExpandedValues, onExpansionChange = props.onExpansionChange;
    var _a = React.useState(new Set(initiallyExpandedValues)), expandedValuesUncontrolled = _a[0], setExpandedValues = _a[1];
    var expandedValues = expandedValuesControlled !== null && expandedValuesControlled !== void 0 ? expandedValuesControlled : expandedValuesUncontrolled;
    var isUncontrolled = !expandedValuesControlled;
    React.useEffect(function () {
        // make sure matched nodes from a search are visible by changing their ancestors to be expanded
        isUncontrolled && matchedValues.size && setExpandedValues(matchedValuesAncestors);
    }, [isUncontrolled, matchedValues, matchedValuesAncestors]);
    var toggleExpansion = React.useCallback(function (value, expand) {
        if (!value) {
            return;
        }
        var alreadyExpanded = expandedValues.has(value);
        if (expand !== false && !alreadyExpanded) {
            var newExpandedValues = new Set(expandedValues);
            newExpandedValues.add(value);
            isUncontrolled && setExpandedValues(newExpandedValues);
            onExpansionChange === null || onExpansionChange === void 0 ? void 0 : onExpansionChange(value);
        }
        else if (!expand && alreadyExpanded) {
            var newExpandedValues = new Set(expandedValues);
            newExpandedValues.delete(value);
            isUncontrolled && setExpandedValues(newExpandedValues);
            onExpansionChange === null || onExpansionChange === void 0 ? void 0 : onExpansionChange(value);
        }
    }, [expandedValues, isUncontrolled, onExpansionChange]);
    var addToExpandedValues = React.useCallback(function (valuesToAdd) {
        var added = false;
        if (isUncontrolled) {
            var newExpandedValues_1 = new Set(expandedValues);
            valuesToAdd.forEach(function (value) {
                if (!expandedValues.has(value)) {
                    newExpandedValues_1.add(value);
                    added = true;
                }
            });
            added && setExpandedValues(newExpandedValues_1);
        }
    }, [expandedValues, isUncontrolled]);
    return { addToExpandedValues: addToExpandedValues, expandedValues: expandedValues, setExpandedValues: setExpandedValues, toggleExpansion: toggleExpansion };
};
var getInitialSelection = function (props) {
    var initiallySelectedValue = props.initiallySelectedValue, initiallySelectedValues = props.initiallySelectedValues, multiselect = props.multiselect;
    if (multiselect) {
        return initiallySelectedValues || [];
    }
    else {
        return initiallySelectedValue ? [initiallySelectedValue] : [];
    }
};
var useSelectedValueAsSet = function (selectedValue) {
    return React.useMemo(function () {
        if (selectedValue === undefined) {
            return undefined;
        }
        else if (selectedValue === null) {
            return new Set();
        }
        else {
            return new Set([selectedValue]);
        }
    }, [selectedValue]);
};
var useSelectionState = function (treeHierarchyInfo, props) {
    var _a;
    var allSelectableLeafValues = treeHierarchyInfo.allSelectableLeafValues, allSelectableValues = treeHierarchyInfo.allSelectableValues, nodeMapByValue = treeHierarchyInfo.nodeMapByValue;
    var multiselect = props.multiselect, onSelectionChange = props.onSelectionChange, selectedValuesControlled = props.selectedValues;
    var _b = React.useState(new Set(getInitialSelection(props))), selectedValuesUncontrolled = _b[0], setSelectedValues = _b[1];
    var selectedValueAsSet = useSelectedValueAsSet(props.selectedValue);
    var selectedValues = (_a = (multiselect ? selectedValuesControlled : selectedValueAsSet)) !== null && _a !== void 0 ? _a : selectedValuesUncontrolled;
    var isUncontrolled = !selectedValuesControlled && !selectedValueAsSet;
    var toggleSelection = React.useCallback(function (value, select) {
        if (!value || !allSelectableValues.has(value)) {
            return;
        }
        var node = nodeMapByValue.get(value);
        if (!node) {
            return;
        }
        var newSelectedValues = multiselect ? new Set(selectedValues) : new Set();
        var alreadySelected = selectedValues.has(value);
        var descendantLeafValues = node.children.length ? TreeHelpers_1.getDescendantLeafValues(nodeMapByValue, value) : [];
        var partiallySelected = multiselect && descendantLeafValues.filter(function (v) { return selectedValues.has(v); }).length > 0;
        if (select !== false && !(alreadySelected || partiallySelected)) {
            if (isUncontrolled) {
                var selectableValues_1 = multiselect ? allSelectableLeafValues : allSelectableValues;
                selectableValues_1.has(value) && newSelectedValues.add(value);
                multiselect && descendantLeafValues.forEach(function (v) {
                    selectableValues_1.has(v) && newSelectedValues.add(v); // add values of all selectable descendants
                });
                setSelectedValues(newSelectedValues);
            }
            onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange(value);
        }
        else if (!select && (alreadySelected || partiallySelected)) {
            if (isUncontrolled) {
                newSelectedValues.delete(value);
                multiselect && descendantLeafValues.forEach(function (v) {
                    newSelectedValues.delete(v); // remove values of all descendants (no need to check for selectable because it's safe to delete values not in the set)
                });
                setSelectedValues(newSelectedValues);
            }
            onSelectionChange === null || onSelectionChange === void 0 ? void 0 : onSelectionChange(value);
        }
    }, [allSelectableLeafValues, allSelectableValues, isUncontrolled, multiselect, nodeMapByValue, onSelectionChange, selectedValues]);
    return { selectedValues: selectedValues, setSelectedValues: setSelectedValues, toggleSelection: toggleSelection };
};
var usePseudoFocusState = function (focusableValues, selectedValues, treeId, hideSearchField) {
    var _a = React.useState(undefined), activeValue = _a[0], setActiveValue = _a[1];
    var updateActiveValue = React.useCallback(function (value) {
        if (value !== activeValue) {
            setActiveValue(value);
        }
    }, [activeValue]);
    React.useLayoutEffect(function () {
        activeValue && DomUtil_1.scrollIntoView(document.getElementById(exports.treeItemId(treeId, activeValue)));
    }, [activeValue, treeId]);
    React.useEffect(function () {
        // make sure the active value is actually valid
        if (!activeValue || !focusableValues.includes(activeValue)) {
            if (hideSearchField && focusableValues.length) {
                updateActiveValue(focusableValues.find(function (v) { return selectedValues.has(v); }) || focusableValues[0]);
            }
            else {
                updateActiveValue(undefined);
            }
        }
    }, [activeValue, focusableValues, hideSearchField, selectedValues, updateActiveValue]);
    return { activeValue: activeValue, setActiveValue: setActiveValue, updateActiveValue: updateActiveValue };
};
var useTreeChildElements = function (treeId, treeHierarchyInfo, treeState, multiselect) {
    var allParentValuesLoadingWithin = treeHierarchyInfo.allParentValuesLoadingWithin, allSelectableValues = treeHierarchyInfo.allSelectableValues, nodeMapByValue = treeHierarchyInfo.nodeMapByValue, topLevelNodes = treeHierarchyInfo.topLevelNodes;
    var activeValue = treeState.activeValue, expandedValues = treeState.expandedValues, selectedValues = treeState.selectedValues, toggleExpansion = treeState.toggleExpansion;
    var renderTreeItems = React.useCallback(function (nodes, level) {
        var setsize = nodes.filter(function (node) { return ListItemUtils_1.isFocusableItem(node.item); }).length;
        var hiddenElems;
        var posinset = 0;
        return React.createElement(React.Fragment, null, nodes.map(function (node, index) {
            var _a, _b, _c;
            if (!node.item.value || node.item.separator) {
                hiddenElems = undefined;
                return React.createElement("div", { className: TreeStyles_1.SeparatorStyleName, key: "separator%%" + index, role: 'separator', style: (_a = {}, _a["" + TreeStyles_1.TreeNodeLevelCSSVarName] = level, _a) });
            }
            if (node.item.group) {
                // need visually hidden elements to allow the section title to be read by assistive technology
                hiddenElems = hiddenElemsForSectionTitle(node.item.content || node.item.value);
                return React.createElement("div", { children: node.item.content || node.item.value, className: TreeStyles_1.SectionTitleStyleName, key: "section%%" + index, style: (_b = {}, _b["" + TreeStyles_1.TreeNodeLevelCSSVarName] = level, _b) });
            }
            var isSelected = selectedValues.has(node.item.value);
            var isSelectionIndeterminate = false;
            if (multiselect && node.children.length && !node.item.disabled) {
                var descendantLeafValues = TreeHelpers_1.getDescendantLeafValues(nodeMapByValue, node.item.value);
                var numDescendantsSelected = descendantLeafValues.filter(function (v) { return selectedValues.has(v); }).length;
                isSelected = numDescendantsSelected === descendantLeafValues.length;
                if (numDescendantsSelected > 0 && numDescendantsSelected < descendantLeafValues.length) {
                    isSelectionIndeterminate = true;
                }
            }
            posinset++; // one-based
            return React.createElement(TreeItemElem, { "aria-level": level + 1, "aria-posinset": posinset, "aria-setsize": setsize, hiddenElems: hiddenElems, id: exports.treeItemId(treeId, node.item.value), isActive: activeValue === node.item.value, isExpanded: expandedValues.has(node.item.value), isSelected: isSelected, isSelectionIndeterminate: isSelectionIndeterminate, isUnselectable: !allSelectableValues.has(node.item.value), item: node.item, key: node.item.value, loadingWithin: allParentValuesLoadingWithin.has(node.item.value), nestedItems: node.children.length ? renderTreeItems(node.children, level + 1) : undefined, style: (_c = {}, _c["" + TreeStyles_1.TreeNodeLevelCSSVarName] = level, _c), toggleExpansion: toggleExpansion });
        }));
    }, [activeValue, allParentValuesLoadingWithin, allSelectableValues, expandedValues, multiselect, nodeMapByValue, selectedValues, toggleExpansion, treeId]);
    var childElements = React.useMemo(function () { return renderTreeItems(topLevelNodes, 0); }, [renderTreeItems, topLevelNodes]);
    return childElements;
};
var hiddenElemsForSectionTitle = function (content) {
    var stringParts = Localize_1.Messages.treeSectionPrefix({ ITEM_NAME: '%%ITEM_NAME%%', SECTION_NAME: '%%SECTION_NAME%%' }).split('%%ITEM_NAME%%');
    return stringParts.map(function (stringPart, index) {
        if (stringPart.includes('%%SECTION_NAME%%')) {
            var stringPartsAroundSectionTitleContent = stringPart.split('%%SECTION_NAME%%');
            return (React.createElement("span", { key: index, style: LiveAnnouncer_1.VisuallyHiddenStyle },
                stringPartsAroundSectionTitleContent[0],
                content,
                stringPartsAroundSectionTitleContent[1]));
        }
        else {
            return stringPart.length ? React.createElement("span", { key: index, style: LiveAnnouncer_1.VisuallyHiddenStyle }, stringPart) : React.createElement(React.Fragment, null);
        }
    });
};
var TreeItemElem = function (props) {
    var _a, _b;
    // eslint-disable-next-line max-len
    var hiddenElems = props.hiddenElems, isActive = props.isActive, isExpanded = props.isExpanded, isSelected = props.isSelected, isSelectionIndeterminate = props.isSelectionIndeterminate, isUnselectable = props.isUnselectable, item = props.item, loadingWithin = props.loadingWithin, nestedItems = props.nestedItems, toggleExpansion = props.toggleExpansion, htmlAttrs = tslib_1.__rest(props, ["hiddenElems", "isActive", "isExpanded", "isSelected", "isSelectionIndeterminate", "isUnselectable", "item", "loadingWithin", "nestedItems", "toggleExpansion"]);
    var groupDomId = React.useState(DomUtil_1.generateId())[0];
    var hasNestedItems = !!nestedItems;
    var contentLoading = TreeHelpers_1.isContentLoading(item);
    return React.createElement(React.Fragment, null,
        React.createElement("li", tslib_1.__assign({}, htmlAttrs, (_a = {}, _a[List_1.ItemValueAttr] = item.value, _a), { "aria-checked": isUnselectable ? undefined : isSelected, "aria-disabled": (!hasNestedItems && (item.disabled || contentLoading)) ? true : undefined, "aria-expanded": hasNestedItems ? isExpanded : undefined, "aria-owns": hasNestedItems && isExpanded ? groupDomId : undefined, "aria-selected": isActive, className: TypeStyle.classes(TreeStyles_1.TreeItemStyleName, (_b = {},
                _b[TreeStyles_1.TreeNodeLoadingClassName] = contentLoading,
                _b[TreeStyles_1.TreeNodeDisabledClassName] = item.disabled || contentLoading || loadingWithin,
                _b)), "data-tb-test-id": item.testId, role: 'treeitem', title: item.tooltip }),
            React.createElement("span", { className: TreeStyles_1.ChevronContainerStyleName }, // need a container to preserve space even when the chevron icon is not shown so that the content of sibling items can line up
            hasNestedItems &&
                React.createElement(ArrowChevronRightIcon_1.ArrowChevronRightIcon, { "aria-hidden": true, onClick: function (e) {
                        toggleExpansion(item.value);
                        e.stopPropagation();
                    }, size: TreeStyles_1.DefaultIconSize })),
            React.createElement("span", { className: TreeStyles_1.ContentContainerStyleName },
                React.createElement("span", { className: TreeStyles_1.LabelContainerStyleName }, hiddenElems === null || hiddenElems === void 0 ? void 0 :
                    hiddenElems[0],
                    item.content || item.value, hiddenElems === null || hiddenElems === void 0 ? void 0 :
                    hiddenElems[1]),
                React.createElement("span", { className: TypeStyle.classes(TreeStyles_1.CheckContainerStyleName) }, !isUnselectable && (isSelectionIndeterminate
                    ? React.createElement(OperationsSubtractMinusIcon_1.OperationsSubtractMinusIcon, { "aria-hidden": true, size: TreeStyles_1.CheckIconSize })
                    : isSelected && React.createElement(NotificationCheckIcon_1.NotificationCheckIcon, { "aria-hidden": true, size: TreeStyles_1.CheckIconSize }))))),
        hasNestedItems && isExpanded &&
            React.createElement("ul", { children: nestedItems, id: groupDomId, role: 'group' }));
};
TreeItemElem.displayName = 'TreeItemElem';
//# sourceMappingURL=TreeWidget.js.map